import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Lightbulb,
  Play,
  Send,
  FlaskConical,
  Pencil,
  Check,
  X,
  Activity,
  TrendingUp,
  XCircle as XCircleIcon,
  Shield,
  Brain,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  History,
  Archive,
  RotateCcw,
  CheckCircle,
  Rocket,
} from 'lucide-react';
import {
  useStrategy,
  useUpdateStrategy,
  usePublishStrategy,
  useRunBacktest,
  useApplySuggestedRules,
  useGenerateSuggestions,
  useStrategyVersions,
  useStrategyVersion,
  useArchiveStrategy,
  useApplyVersion,
  type BacktestParams,
} from '@/hooks/use-strategies';
import { useMt5Accounts } from '@/hooks/use-accounts';
import { useJob, BACKTEST_STAGES, SUGGESTION_STAGES } from '@/hooks/use-job';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Modal } from '@/components/ui/modal';
import { TradeHistoryModal, formatDuration } from '@/components/ui/trade-history-modal';
import { formatUsd, formatPnl, formatPct, pnlColor, timeAgo, friendlyError } from '@/lib/format';
import { condToSentence, fmtLot, extractIndicators } from '@/lib/condition-text';
import { getMaxBacktestDays } from '@/lib/symbol-categories';
import type { BacktestReport, BacktestSummary, SuggestionMode } from '@/types/api';
import { ModeSelector } from '@/components/ui/mode-selector';
import { EquityCurve } from '@/components/equity-curve';

export default function StrategyDetailPage() {
  const { id } = useParams();
  const [showArchive, setShowArchive] = useState(false);
  const [hasBacktestResult, setHasBacktestResult] = useState(false);
  const [showBacktestMobile, setShowBacktestMobile] = useState(false);
  const archiveStrategy = useArchiveStrategy();

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  const { data: strategy, isLoading, isFetching, error } = useStrategy(id!);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !strategy) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load strategy</p>
        <Link to="/strategies" className="inline-block mt-3">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={14} />
            Back to Strategies
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Back Link */}
      <Link
        to="/strategies"
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors"
      >
        <ArrowLeft size={12} />
        All Strategies
      </Link>


      {/* Two-column layout on desktop */}
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Left: Header + Story + Versions */}
        <div className="flex-1 min-w-0 space-y-5">
          <StrategyHeader strategy={strategy} />
          <StoryTimeline strategy={strategy} isRefreshing={isFetching && !isLoading} />
          <div className="pl-[52px] space-y-4">
            {strategy.latest_version > 0 && <VersionHistory strategyId={strategy.id} latestVersion={strategy.latest_version} />}

            {strategy.status !== 'ARCHIVED' && (
              <button
                type="button"
                onClick={() => setShowArchive(true)}
                className="flex items-center gap-2 text-[10px] font-semibold text-ink-4 hover:text-red transition-colors cursor-pointer"
              >
                <Archive size={14} />
                Archive this strategy
              </button>
            )}
          </div>
        </div>

        {/* Right: Backtest panel — inline on desktop, fullscreen modal on mobile */}
        <div className="hidden lg:block lg:w-[30%] lg:flex-shrink-0">
          <div className={hasBacktestResult ? '' : 'lg:sticky lg:top-4'}>
            <BacktestPanel strategyId={strategy.id} strategy={strategy} onHasWarnings={setHasBacktestResult} />
          </div>
        </div>
      </div>

      {/* Mobile backtest FAB — above mobile nav */}
      <button
        onClick={() => setShowBacktestMobile(true)}
        className="lg:hidden fixed bottom-20 right-4 z-50 w-12 h-12 rounded-full flex items-center justify-center text-white active:scale-95 transition-transform"
        style={{
          background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
          boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)',
        }}
        title="Backtest"
      >
        <FlaskConical size={20} />
      </button>

      {/* Mobile backtest fullscreen modal */}
      {showBacktestMobile && createPortal(
        <div className="fixed inset-0 z-[99999] bg-bg overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-bg/95 backdrop-blur-sm border-b border-border">
            <h2 className="text-sm font-bold">Backtest</h2>
            <button
              onClick={() => setShowBacktestMobile(false)}
              className="text-ink-3 hover:text-ink p-2 -mr-2"
            >
              <X size={20} />
            </button>
          </div>
          <div className="p-4 pb-24">
            <BacktestPanel strategyId={strategy.id} strategy={strategy} onHasWarnings={setHasBacktestResult} />
          </div>
        </div>,
        document.body,
      )}

      <Modal
        open={showArchive}
        onClose={() => setShowArchive(false)}
        title="Archive Strategy"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowArchive(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red hover:bg-red/10"
              onClick={() => {
                archiveStrategy.mutate(strategy.id);
                setShowArchive(false);
              }}
              disabled={archiveStrategy.isPending}
            >
              <Archive size={12} />
              Archive
            </Button>
          </>
        }
      >
        <p className="text-xs text-ink-3">
          Archiving will make this strategy read-only. You won't be able to edit
          or publish new versions. Existing bots using this strategy will not be affected.
        </p>
      </Modal>
    </div>
  );
}

// ─── Header ─────────────────────────────────────────

function StrategyHeader({
  strategy,
}: {
  strategy: NonNullable<ReturnType<typeof useStrategy>['data']>;
}) {
  const navigate = useNavigate();
  const publishStrategy = usePublishStrategy();
  const updateStrategy = useUpdateStrategy();
  const { data: mt5Accounts } = useMt5Accounts();
  const [showPublish, setShowPublish] = useState(false);
  const [publishSuccessVersion, setPublishSuccessVersion] = useState<number | null>(null);
  const [successAnimating, setSuccessAnimating] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');

  const showSuccessAfterDelay = useCallback((version: number) => {
    // Delay so publish modal closes first
    setTimeout(() => {
      setPublishSuccessVersion(version);
      // Trigger entrance animation on next frame
      requestAnimationFrame(() => setSuccessAnimating(true));
    }, 250);
  }, []);

  const nextVersion = strategy.latest_version + 1;
  const hasUnpublishedChanges = strategy.status === 'DRAFT' && strategy.latest_version > 0;

  // Inline name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(strategy.name);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Inline description editing
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(strategy.description ?? '');
  const descInputRef = useRef<HTMLInputElement>(null);

  // Focus inputs when entering edit mode
  useEffect(() => {
    if (editingName) nameInputRef.current?.focus();
  }, [editingName]);
  useEffect(() => {
    if (editingDesc) descInputRef.current?.focus();
  }, [editingDesc]);

  // Sync local state when strategy data changes (e.g. after mutation)
  useEffect(() => {
    if (!editingName) setNameValue(strategy.name);
  }, [strategy.name, editingName]);
  useEffect(() => {
    if (!editingDesc) setDescValue(strategy.description ?? '');
  }, [strategy.description, editingDesc]);

  const saveName = () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === strategy.name) {
      setNameValue(strategy.name);
      setEditingName(false);
      return;
    }
    updateStrategy.mutate({ id: strategy.id, name: trimmed });
    setEditingName(false);
  };

  const saveDesc = () => {
    const trimmed = descValue.trim();
    if (trimmed === (strategy.description ?? '')) {
      setEditingDesc(false);
      return;
    }
    updateStrategy.mutate({ id: strategy.id, description: trimmed });
    setEditingDesc(false);
  };

  const statusColor =
    strategy.status === 'PUBLISHED'
      ? 'green'
      : strategy.status === 'DRAFT'
        ? 'amber'
        : 'default';

  return (
    <>
      <div className="glass-sm rounded-lg p-5">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-md bg-surface-2 flex items-center justify-center flex-shrink-0 hidden sm:flex">
              <Lightbulb size={20} className="text-orange" />
            </div>
            <div className="min-w-0">
              {/* Editable name */}
              <div className="flex flex-wrap items-center gap-2">
                {editingName ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      ref={nameInputRef}
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveName();
                        if (e.key === 'Escape') {
                          setNameValue(strategy.name);
                          setEditingName(false);
                        }
                      }}
                      className="text-lg font-extrabold tracking-tight bg-transparent border-b border-orange/40 outline-none py-0 px-0.5 text-ink-1 w-[280px]"
                    />
                    <button
                      onClick={saveName}
                      className="text-green hover:text-green/80 transition-colors cursor-pointer"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={() => {
                        setNameValue(strategy.name);
                        setEditingName(false);
                      }}
                      className="text-ink-4 hover:text-red transition-colors cursor-pointer"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingName(true)}
                    className="group flex items-center gap-1.5 cursor-pointer"
                    title="Click to rename"
                  >
                    <h1 className="text-lg font-extrabold tracking-tight">{strategy.name}</h1>
                    <Pencil
                      size={12}
                      className="text-ink-4 opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </button>
                )}
                <Badge color={statusColor}>{strategy.status}</Badge>
                {strategy.latest_version > 0 && (
                  <Badge color="blue">v{strategy.latest_version}</Badge>
                )}
                {strategy.symbol && (
                  <Badge color="blue">{strategy.symbol}</Badge>
                )}
              </div>

              {/* Editable description */}
              {editingDesc ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <input
                    ref={descInputRef}
                    value={descValue}
                    onChange={(e) => setDescValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveDesc();
                      if (e.key === 'Escape') {
                        setDescValue(strategy.description ?? '');
                        setEditingDesc(false);
                      }
                    }}
                    placeholder="Add a description…"
                    className="text-xs text-ink-3 bg-transparent border-b border-orange/30 outline-none py-0 px-0.5 w-[320px]"
                  />
                  <button
                    onClick={saveDesc}
                    className="text-green hover:text-green/80 transition-colors cursor-pointer"
                  >
                    <Check size={12} />
                  </button>
                  <button
                    onClick={() => {
                      setDescValue(strategy.description ?? '');
                      setEditingDesc(false);
                    }}
                    className="text-ink-4 hover:text-red transition-colors cursor-pointer"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingDesc(true)}
                  className="group flex items-center gap-1 mt-0.5 cursor-pointer"
                  title="Click to edit description"
                >
                  <p className="text-xs text-ink-3">
                    {strategy.description || 'Add a description…'}
                  </p>
                  <Pencil
                    size={10}
                    className="text-ink-4 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <MetricsGrid strategy={strategy} />

        {/* Action buttons — bottom right */}
        {strategy.status !== 'ARCHIVED' && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/strategies/${strategy.id}/edit`)}>
              <Pencil size={12} />
              Edit Config
            </Button>
            {strategy.status === 'DRAFT' && (
              <Button size="sm" onClick={() => setShowPublish(true)}>
                <Send size={12} />
                {strategy.latest_version === 0 ? 'Publish v1' : `Publish v${nextVersion}`}
              </Button>
            )}
          </div>
        )}
      </div>

      <Modal
        open={showPublish}
        onClose={() => setShowPublish(false)}
        title={strategy.latest_version === 0 ? 'Publish v1' : `Publish v${nextVersion}`}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowPublish(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                try {
                  await publishStrategy.mutateAsync({ id: strategy.id, changeSummary: changeSummary.trim() || undefined });
                  const publishedVersion = nextVersion;
                  setChangeSummary('');
                  setShowPublish(false);
                  showSuccessAfterDelay(publishedVersion);
                } catch {
                  // error handled by react-query
                }
              }}
              disabled={publishStrategy.isPending}
            >
              <Send size={12} />
              Publish v{nextVersion}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-ink-3">
            This will create an immutable snapshot of the current rules as version {nextVersion}.
            Existing bots will not be affected — they use their own copy of the rules.
          </p>
          {strategy.latest_version > 0 && (
            <div>
              <label className="block text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1.5">
                What changed? <span className="text-ink-4 font-normal">(optional)</span>
              </label>
              <input
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
                placeholder="e.g., Adjusted RSI thresholds, added trailing stop"
                className="w-full bg-glass-card/60 border border-glass-border rounded-lg px-3 py-2 text-xs text-white outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
              />
            </div>
          )}
        </div>
      </Modal>

      {publishSuccessVersion !== null && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/55 backdrop-blur-sm transition-opacity duration-300"
          style={{ zIndex: 10000, opacity: successAnimating ? 1 : 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSuccessAnimating(false);
              setTimeout(() => setPublishSuccessVersion(null), 200);
            }
          }}
        >
          <div
            className={`max-w-sm w-[90vw] bg-bg-2 backdrop-blur-xl border border-glass-border rounded-xl shadow-modal overflow-hidden transition-all duration-300 ${
              successAnimating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2'
            }`}
          >
            {/* Green accent bar */}
            <div className="h-1 bg-gradient-to-r from-green/80 via-green to-green/80" />

            <div className="flex flex-col items-center text-center px-6 pt-7 pb-6 gap-4">
              {/* Animated icon */}
              <div className={`w-14 h-14 rounded-full bg-green/10 border border-green/20 flex items-center justify-center transition-all duration-500 delay-150 ${
                successAnimating ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
              }`}>
                <CheckCircle size={30} className="text-green" />
              </div>

              <div className={`transition-all duration-400 delay-200 ${
                successAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}>
                <p className="text-base font-extrabold text-ink-1 tracking-tight">Strategy Published!</p>
                <p className="text-xs text-ink-3 mt-1.5">
                  Version {publishSuccessVersion} is now live and ready to trade.
                </p>
              </div>

              {/* Next step hint */}
              <div className={`w-full bg-surface/50 border border-glass-border rounded-lg p-3 mt-1 transition-all duration-400 delay-300 ${
                successAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}>
                <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1">Next step</p>
                <p className="text-xs text-ink-2">
                  {mt5Accounts && mt5Accounts.length > 0
                    ? 'Create a bot to start trading with this strategy.'
                    : 'Connect your MT5 broker account to start trading.'}
                </p>
              </div>

              {/* Action buttons */}
              <div className={`flex items-center gap-3 w-full mt-1 transition-all duration-400 delay-[400ms] ${
                successAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setSuccessAnimating(false);
                    setTimeout(() => setPublishSuccessVersion(null), 200);
                  }}
                >
                  Done
                </Button>
                {mt5Accounts && mt5Accounts.length > 0 ? (
                  <Link to="/bots/new" className="flex-1">
                    <Button size="sm" className="w-full">
                      <Rocket size={12} />
                      Create a Bot
                    </Button>
                  </Link>
                ) : (
                  <Link to="/accounts/new" className="flex-1">
                    <Button size="sm" className="w-full">
                      Connect MT5 Account
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {hasUnpublishedChanges && (
        <div className="flex items-center gap-2 bg-amber/10 border border-amber/20 rounded-lg px-3 py-2.5">
          <AlertTriangle size={14} className="text-amber flex-shrink-0" />
          <p className="text-[10px] text-amber font-semibold">
            You have unpublished changes since v{strategy.latest_version}
          </p>
        </div>
      )}

    </>
  );
}

// ─── Metrics Grid (inside header card) ──────────────

function MetricsGrid({
  strategy,
}: {
  strategy: NonNullable<ReturnType<typeof useStrategy>['data']>;
}) {
  const snapshot = strategy.analysis_snapshot as Record<string, unknown> | null;
  const hasEditedAnalysis = !!strategy.edited_analysis;
  const stats = (snapshot?.stats ?? {}) as Record<string, unknown>;

  // Only show trade history stats for uploaded strategies with real analysis data.
  // Custom-built strategies have camelCase stats (totalPnl) from buildMinimalStatsFromRules;
  // uploaded strategies use snake_case (total_pnl). Check total_pnl to detect real trade stats.
  const hasRealStats = typeof stats.total_pnl === 'number';
  if (!snapshot || hasEditedAnalysis || !hasRealStats) return null;

  const pips = stats.pipsStats as any;
  const metrics = [
    { label: 'ROI', value: stats.roi, format: (v: number) => formatPct(v), color: true },
    { label: 'Total P&L', value: stats.total_pnl, format: (v: number) => formatPnl(v), color: true },
    { label: 'Win Rate', value: stats.win_rate, format: (v: number) => `${v.toFixed(1)}%`, color: false },
    { label: 'Drawdown', value: stats.drawdown, format: (v: number) => `${v.toFixed(2)}%`, color: false, fixedColor: 'text-amber' },
    { label: 'Gross Profit', value: stats.gross_profit, format: (v: number) => formatUsd(v), color: false, fixedColor: 'text-green' },
    { label: 'Gross Loss', value: stats.gross_loss, format: (v: number) => formatPnl(v), color: false, fixedColor: 'text-red' },
    { label: 'Duration', value: stats.duration, format: (v: number) => `${v.toFixed(1)}d`, color: false },
    ...(pips?.available ? [
      { label: 'Total Pips', value: pips.totalPips, format: (v: number) => `${v.toFixed(1)}`, color: true },
      { label: 'Avg Pips/Trade', value: pips.avgPipsPerTrade, format: (v: number) => `${v.toFixed(1)}`, color: true },
      { label: 'Expectancy', value: pips.expectancyPips, format: (v: number) => `${v.toFixed(1)} pips`, color: true },
      { label: 'R:R (Pips)', value: pips.riskRewardPips, format: (v: number) => `${v.toFixed(2)}`, color: false },
    ] : []),
  ].filter((m) => typeof m.value === 'number');

  if (metrics.length === 0) return null;

  return (
    <div className="border-t border-border pt-3 mt-3">
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-x-2 gap-y-3">
        {metrics.map((m) => {
          const val = m.value as number;
          const colorClass = m.fixedColor ?? (m.color ? pnlColor(val) : 'text-ink');
          return (
            <div key={m.label} className="text-center">
              <p className="text-[8px] text-ink-4 uppercase tracking-wider">{m.label}</p>
              <p className={`text-xs font-bold font-mono ${colorClass}`}>{m.format(val)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Story Timeline ─────────────────────────────────

type StepColor = 'blue' | 'green' | 'red' | 'cyan' | 'orange';

const stepColors: Record<StepColor, { bg: string; border: string; text: string }> = {
  blue: { bg: 'bg-blue/10', border: 'border-blue/30', text: 'text-blue' },
  green: { bg: 'bg-green/10', border: 'border-green/30', text: 'text-green' },
  red: { bg: 'bg-red/10', border: 'border-red/30', text: 'text-red' },
  cyan: { bg: 'bg-cyan/10', border: 'border-cyan/30', text: 'text-cyan' },
  orange: { bg: 'bg-orange/10', border: 'border-orange/30', text: 'text-orange' },
};

function StoryTimeline({
  strategy,
  isRefreshing = false,
}: {
  strategy: NonNullable<ReturnType<typeof useStrategy>['data']>;
  isRefreshing?: boolean;
}) {
  const snapshot = strategy.analysis_snapshot as Record<string, unknown> | null;
  const editedAnalysis = strategy.edited_analysis as Record<string, unknown> | null;
  const rawStats = (snapshot?.stats ?? {}) as Record<string, unknown>;
  // Strategy.symbol is the source of truth — override analysis_snapshot.stats.symbol
  const stats = { ...rawStats, symbol: strategy.symbol || rawStats.symbol } as Record<string, unknown>;
  // Use edited analysis if available (rules were modified), otherwise original breakdown
  const breakdown = (editedAnalysis ?? snapshot?.strategy_breakdown ?? {}) as Record<string, unknown>;
  const rules = strategy.rules as Record<string, unknown> | null;
  const suggestions = snapshot?.suggestions as Record<string, unknown> | undefined;
  const suggestedBreakdown = snapshot?.suggested_breakdown as Record<string, unknown> | undefined;

  const steps: {
    number: number;
    title: string;
    icon: React.ReactNode;
    color: StepColor;
    description: React.ReactNode;
    content: React.ReactNode;
  }[] = [];

  // Step 1: Watch the Market
  steps.push({
    number: 1,
    title: 'Watch the Market',
    icon: <Activity size={14} />,
    color: 'blue',
    description: <WatchMarketDesc stats={stats} rules={rules} />,
    content: <WatchMarketCard stats={stats} rules={rules} />,
  });

  // Step 2: Decide When to Enter
  if (rules) {
    steps.push({
      number: 2,
      title: 'Decide When to Enter',
      icon: <TrendingUp size={14} />,
      color: 'green',
      description: <EntryDesc breakdown={breakdown} rules={rules} />,
      content: <EntryCard rules={rules} />,
    });
  }

  // Step 3: Decide When to Exit
  if (rules) {
    steps.push({
      number: 3,
      title: 'Decide When to Exit',
      icon: <XCircleIcon size={14} />,
      color: 'red',
      description: <ExitDesc breakdown={breakdown} rules={rules} />,
      content: <ExitCard rules={rules} />,
    });
  }

  // Step 4: Risk Boundaries
  if (rules) {
    steps.push({
      number: 4,
      title: 'Risk Boundaries',
      icon: <Shield size={14} />,
      color: 'cyan',
      description: <RiskDesc rules={rules} breakdown={breakdown} />,
      content: <RiskCard rules={rules} breakdown={breakdown} />,
    });
  }

  // Step 5: AI Suggestions (always visible for DRAFT, or when suggestions exist)
  const notes = suggestions?.notes as string[] | undefined;
  const suggestionMode = snapshot?.suggestion_mode as string | undefined;
  const suggestionCapital = snapshot?.suggestion_capital as number | undefined;
  const suggestionLeverage = snapshot?.suggestion_leverage as number | undefined;
  const modeLabel = suggestionMode ? suggestionMode.charAt(0).toUpperCase() + suggestionMode.slice(1) : 'Optimal';
  const hasSuggestions = (notes && notes.length > 0) || !!suggestedBreakdown;

  const capitalLeverageDesc = suggestionCapital || suggestionLeverage
    ? ` ($${suggestionCapital ?? '—'} capital, 1:${suggestionLeverage ?? '—'} leverage)`
    : '';

  if (strategy.status !== 'ARCHIVED' || hasSuggestions) {
    steps.push({
      number: 5,
      title: 'AI Suggestions',
      icon: <Brain size={14} />,
      color: 'orange',
      description: (
        <p className="text-xs text-ink-2 leading-relaxed">
          {hasSuggestions
            ? (suggestedBreakdown?.summary
              ? `AI analyzed this strategy in ${modeLabel} mode${capitalLeverageDesc} and proposed specific improvements.`
              : `Improvements detected (${modeLabel} mode${capitalLeverageDesc}) that could enhance this strategy's performance.`)
            : 'Generate AI-powered suggestions to improve this strategy.'}
        </p>
      ),
      content: hasSuggestions ? (
        <AISuggestionsCard
          suggestions={suggestions}
          suggestedBreakdown={suggestedBreakdown}
          suggestionMode={suggestionMode}
          suggestionCapital={suggestionCapital}
          suggestionLeverage={suggestionLeverage}
          strategyId={strategy.id}
          strategyStatus={strategy.status}
          hasSuggestedRules={!!snapshot?.suggested_rules}
          suggestionsApplied={!!snapshot?.suggestions_applied}
        />
      ) : (
        <GenerateSuggestionsForm strategyId={strategy.id} />
      ),
    });
  }

  const cardLabels: Record<string, string> = {
    'Watch the Market': 'Market Configuration',
    'Decide When to Enter': 'Entry Rules',
    'Decide When to Exit': 'Exit Rules',
    'Risk Boundaries': 'Risk Management',
    'AI Suggestions': 'Improvement Notes',
  };

  return (
    <div className={`relative transition-opacity duration-300 ${isRefreshing ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
      {isRefreshing && (
        <div className="flex items-center gap-2 mb-4 text-[10px] text-orange font-semibold">
          <Spinner size="sm" />
          Updating analysis...
        </div>
      )}

      {steps.map((step, idx) => {
        const colors = stepColors[step.color];
        const isLast = idx === steps.length - 1;
        return (
          <div key={step.number} className="relative pl-[52px] pb-9">
            {/* Connector line (gradient, hidden on last) */}
            {!isLast && (
              <div
                className="absolute left-[18px] top-[38px] bottom-0 w-[2px]"
                style={{ background: 'linear-gradient(to bottom, rgba(249,115,22,0.2), var(--color-surface))' }}
              />
            )}

            {/* Number badge — rounded square like viewer */}
            <div
              className={`absolute left-0 top-0 w-[38px] h-[38px] rounded-[11px] flex items-center justify-center z-10 text-sm font-extrabold text-white shadow-lg`}
              style={{ background: `var(--color-${step.color})`, boxShadow: '0 0 16px rgba(0,0,0,0.3)' }}
            >
              {step.number}
            </div>

            {/* Title */}
            <h3 className="text-[17px] font-extrabold tracking-tight mb-1 pt-[7px]">
              {step.title}
            </h3>

            {/* Description */}
            <div className="mb-3">{step.description}</div>

            {/* Card */}
            <div className="glass-sm rounded-[14px] overflow-hidden border border-glass-border">
              {/* Card header */}
              <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
                <span className={colors.text}>{step.icon}</span>
                <span className="text-[10px] font-bold uppercase tracking-[0.8px] text-ink-3">
                  {cardLabels[step.title] ?? step.title}
                </span>
              </div>
              {/* Card body */}
              <div className="px-4 py-3.5">
                {step.content}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Watch the Market ────────────────────────

function WatchMarketDesc({
  stats,
  rules,
}: {
  stats: Record<string, unknown>;
  rules: Record<string, unknown> | null;
}) {
  const marketData = (rules?.market_data ?? {}) as Record<string, unknown>;
  const indicators = rules ? extractIndicators(rules) : [];

  const hasSymbol = !!stats.symbol;

  return (
    <p className="text-xs text-ink-2 leading-relaxed">
      {hasSymbol ? (
        <>The bot connects to <strong className="text-ink">{String(stats.symbol)}</strong> and reads</>
      ) : (
        <>The bot reads</>
      )}
      {' '}price data on the <strong className="text-ink">{String(marketData.timeframe || '?')}</strong> timeframe.
      {indicators.length > 0 && (
        <>
          {' '}It calculates{' '}
          {indicators.map((ind, i) => (
            <span key={i}>
              {i > 0 && (i === indicators.length - 1 ? ' and ' : ', ')}
              <span className="inline font-bold font-mono text-[10px] px-1.5 py-px rounded bg-violet/10 text-violet">{ind}</span>
            </span>
          ))}
          {' '}on each candle.
        </>
      )}
    </p>
  );
}

function WatchMarketCard({
  stats,
  rules,
}: {
  stats: Record<string, unknown>;
  rules: Record<string, unknown> | null;
}) {
  const marketData = (rules?.market_data ?? {}) as Record<string, unknown>;
  const indicators = rules ? extractIndicators(rules) : [];
  const dirRatio = stats.direction_ratio as Record<string, number> | undefined;

  return (
    <div className="space-y-1.5">
      {!!stats.symbol && (
        <ParamRow label="Instrument" value={<span className="text-blue font-semibold">{String(stats.symbol)}</span>} />
      )}
      <ParamRow label="Timeframe" value={String(marketData.timeframe || '?')} />
      {indicators.length > 0 && (
        <ParamRow
          label="Indicators"
          value={
            <span className="flex items-center gap-1 flex-wrap justify-end text-violet">
              {indicators.join(', ')}
            </span>
          }
        />
      )}
      {!!stats.direction && (
        <ParamRow
          label="Direction Bias"
          value={
            <span className="flex items-center gap-1.5">
              <span className="font-semibold">{String(stats.direction)}</span>
              {dirRatio && (
                <span className="text-ink-4 text-[9px]">
                  ({((dirRatio.BUY ?? 0) * 100).toFixed(0)}% buy / {((dirRatio.SELL ?? 0) * 100).toFixed(0)}% sell)
                </span>
              )}
            </span>
          }
        />
      )}
    </div>
  );
}

// ─── Step 2: Entry ───────────────────────────────────

function EntryDesc({
  breakdown,
  rules,
}: {
  breakdown: Record<string, unknown>;
  rules: Record<string, unknown>;
}) {
  const entry = rules.entry as Record<string, unknown> | undefined;
  const grid = entry?.grid as Record<string, unknown> | undefined;
  const entryLogic = breakdown.entry_logic as string | undefined;

  if (entryLogic) {
    return <p className="text-xs text-ink-2 leading-relaxed">{entryLogic}</p>;
  }

  if (grid?.enabled) {
    return (
      <p className="text-xs text-ink-2 leading-relaxed">
        When there are <strong className="text-ink">no open positions</strong>, the bot picks a
        direction using indicator signals, then opens a{' '}
        <strong className="text-ink">grid of {String(grid.maxGridLevels)} levels</strong>.
      </p>
    );
  }

  return (
    <p className="text-xs text-ink-2 leading-relaxed">
      When conditions are met, the bot opens a position in the detected direction.
    </p>
  );
}

function EntryCard({ rules }: { rules: Record<string, unknown> }) {
  const entry = rules.entry as Record<string, unknown> | undefined;
  const entryRules: Record<string, unknown>[] = Array.isArray(entry?.rules) ? entry.rules : [];
  const grid = entry?.grid as Record<string, unknown> | undefined;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-3">
      {entryRules.slice(0, expanded ? entryRules.length : 3).map((rule, i) => {
        const direction = String(rule.direction);
        const dirColor = direction === 'BUY' ? 'text-green' : direction === 'SELL' ? 'text-red' : 'text-violet';
        const dirLabel = direction === 'SIGNAL' ? 'Bi-directional' : direction;

        return (
          <div key={i} className="space-y-2">
            {/* SIGNAL direction: split Buy/Sell cards */}
            {direction === 'SIGNAL' && !!(rule.buyCondition || rule.sellCondition) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px]">
                <div className="glass-sm rounded-[12px] p-3 border border-glass-border">
                  <p className="text-[10px] font-bold text-green uppercase tracking-[0.5px] mb-2 flex items-center gap-1.5">Buy When</p>
                  <div className="text-[11px] text-ink-2 leading-[1.7] py-[6px]">
                    {rule.buyCondition ? condToSentence(rule.buyCondition) : (
                      <em className="text-ink-4">No condition</em>
                    )}
                  </div>
                </div>
                <div className="glass-sm rounded-[12px] p-3 border border-glass-border">
                  <p className="text-[10px] font-bold text-red uppercase tracking-[0.5px] mb-2 flex items-center gap-1.5">Sell When</p>
                  <div className="text-[11px] text-ink-2 leading-[1.7] py-[6px]">
                    {rule.sellCondition ? condToSentence(rule.sellCondition) : (
                      <em className="text-ink-4">No condition</em>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* BUY/SELL: single condition line */
              <div className="text-[11px] text-ink-2 leading-[1.7] py-[6px]">
                <strong className={dirColor}>{dirLabel}:</strong>{' '}
                {condToSentence(rule.conditions) ?? <em className="text-ink-4">Always active</em>}
              </div>
            )}

            {/* Common condition (for SIGNAL entries that also have conditions) */}
            {direction === 'SIGNAL' && !!rule.conditions && (
              <div className="text-[11px] text-ink-2 leading-[1.7] py-[6px]">
                <strong className="text-violet">Common:</strong>{' '}
                {condToSentence(rule.conditions)}
              </div>
            )}

            {/* Params */}
            <div className="space-y-1">
              <ParamRow label="Lot Sizing" value={fmtLot(rule.lotSizing)} />
              {!!rule.cooldownCandles && (
                <ParamRow label="Cooldown" value={`${rule.cooldownCandles} candles`} />
              )}
              {!!rule.maxPositions && (
                <ParamRow label="Max Positions" value={String(rule.maxPositions)} />
              )}
            </div>

            {/* Divider between rules */}
            {i < entryRules.length - 1 && <div className="border-t border-border" />}
          </div>
        );
      })}

      {entryRules.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[10px] text-orange font-semibold cursor-pointer flex items-center gap-1"
        >
          {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          {expanded ? 'Show less' : `+${entryRules.length - 3} more rules`}
        </button>
      )}

      {/* Grid Config */}
      {grid?.enabled === true && (
        <>
          <div>
            <p className="text-[9px] font-bold text-ink-4 uppercase tracking-[0.6px] pt-2 mt-1 pb-1">Grid Configuration</p>
            <div className="space-y-1">
              <ParamRow label="Direction" value={String(grid.direction ?? '?')} />
              <ParamRow
                label="Step Size"
                value={
                  grid.stepType === 'ABSOLUTE'
                    ? `$${grid.stepValue}`
                    : grid.stepType === 'PERCENT'
                      ? `${grid.stepValue}%`
                      : `${grid.stepValue}× ATR`
                }
              />
              <ParamRow label="Max Grid Levels" value={String(grid.maxGridLevels)} />
              {!!grid.lotSizing && <ParamRow label="Grid Lot Sizing" value={fmtLot(grid.lotSizing)} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Step 3: Exit ────────────────────────────────────

function ExitDesc({
  breakdown,
  rules,
}: {
  breakdown: Record<string, unknown>;
  rules: Record<string, unknown>;
}) {
  const exit = rules.exit as Record<string, unknown> | undefined;
  const exitLogic = breakdown.exit_logic as string | undefined;

  if (exitLogic) {
    return <p className="text-xs text-ink-2 leading-relaxed">{exitLogic}</p>;
  }

  const equitySl = exit?.equityStopLossPct as number | undefined;
  const trailingStop = exit?.trailingStop as Record<string, unknown> | undefined;

  return (
    <p className="text-xs text-ink-2 leading-relaxed">
      Positions are closed when exit conditions are met.
      {!!trailingStop?.enabled && ' A trailing stop also protects profits as they grow.'}
      {equitySl != null && (
        <> If equity drops below <strong className="text-red">{equitySl}%</strong>, it&apos;s an emergency stop.</>
      )}
    </p>
  );
}

function ExitCard({ rules }: { rules: Record<string, unknown> }) {
  const exit = rules.exit as Record<string, unknown> | undefined;
  const exitRules: Record<string, unknown>[] = Array.isArray(exit?.rules) ? exit.rules : [];
  const trailingStop = exit?.trailingStop as Record<string, unknown> | undefined;
  const equitySl = exit?.equityStopLossPct as number | undefined;

  return (
    <div className="space-y-2">
      {/* Exit rules */}
      {exitRules.map((rule, i) => {
        const action = String(rule.action ?? '');
        const actionColor = action === 'CLOSE_ALL' ? 'red' : 'amber';

        return (
          <div key={i} className="space-y-1">
            <div className="text-[11px] text-ink-2 leading-[1.7] py-[6px]">
              <strong className="text-ink">{String(rule.name || rule.id || `Exit ${i + 1}`)}</strong>
              {' — '}
              {condToSentence(rule.conditions) ?? <em className="text-ink-4">Always active</em>}
            </div>
            <ParamRow
              label="Action"
              value={<Badge color={actionColor}>{action.replace(/_/g, ' ')}</Badge>}
            />
          </div>
        );
      })}

      {/* Equity SL */}
      {equitySl != null && (
        <ParamRow
          label="Equity Stop Loss"
          value={<span className="text-red font-semibold">{equitySl}%</span>}
        />
      )}

      {/* Trailing Stop */}
      {!!trailingStop?.enabled && (
        <>
          <div>
            <p className="text-[9px] font-bold text-ink-4 uppercase tracking-[0.6px] pt-2 mt-1 pb-1">Trailing Stop</p>
            <div className="space-y-1">
              <ParamRow label="Type" value={<Badge color="violet">{String(trailingStop.type ?? '').replace(/_/g, ' ')}</Badge>} />
              <ParamRow
                label="Distance"
                value={
                  trailingStop.type === 'ATR_MULTIPLE'
                    ? `${trailingStop.value}× ATR(${trailingStop.atrPeriod ?? 14})`
                    : trailingStop.type === 'PERCENT'
                      ? `${trailingStop.value}%`
                      : `${trailingStop.value} pts`
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Step 4: Risk ────────────────────────────────────

function RiskDesc({
  rules,
  breakdown,
}: {
  rules: Record<string, unknown>;
  breakdown: Record<string, unknown>;
}) {
  const risk = rules.risk as Record<string, unknown> | undefined;
  const riskProfile = (breakdown.risk_profile ?? {}) as Record<string, unknown>;

  return (
    <p className="text-xs text-ink-2 leading-relaxed">
      The bot never exceeds <strong className="text-ink">{String(risk?.maxTotalPositions ?? '?')} positions</strong> at
      any time. Risk factor is set to <strong className="text-ink">{String(risk?.riskFactor ?? '?')}×</strong>.
      {typeof riskProfile.summary === 'string' && ` ${riskProfile.summary}`}
    </p>
  );
}

function RiskCard({
  rules,
  breakdown,
}: {
  rules: Record<string, unknown>;
  breakdown: Record<string, unknown>;
}) {
  const risk = rules.risk as Record<string, unknown> | undefined;
  const riskProfile = (breakdown.risk_profile ?? {}) as Record<string, unknown>;
  const strengths = Array.isArray(breakdown.strengths) ? breakdown.strengths : [];
  const weaknesses = Array.isArray(breakdown.weaknesses) ? breakdown.weaknesses : [];
  const level = String(riskProfile.level || 'MEDIUM');

  return (
    <div className="space-y-2">
      <ParamRow label="Max Total Positions" value={String(risk?.maxTotalPositions ?? '?')} />
      <ParamRow label="Risk Factor" value={`${String(risk?.riskFactor ?? '?')}×`} />
      <ParamRow
        label="Risk Level"
        value={
          <Badge color={level === 'LOW' ? 'green' : level === 'MEDIUM' ? 'amber' : level === 'HIGH' ? 'orange' : 'red'}>
            {level}
          </Badge>
        }
      />

      {/* Strengths */}
      {strengths.length > 0 && (
        <div className="mt-2.5">
          <p className="text-[9px] font-bold text-ink-4 uppercase tracking-[0.6px] pt-2 mt-1 pb-1" style={{ color: 'var(--color-green)' }}>
            Strengths
          </p>
          {strengths.slice(0, 3).map((s, i) => (
            <div key={i} className="relative text-[10px] text-ink-2 leading-[1.5] py-[3px] pl-3.5">
              <span className="absolute left-0 top-[9px] w-[5px] h-[5px] rounded-full bg-green" />
              {String(s)}
            </div>
          ))}
        </div>
      )}

      {/* Weaknesses */}
      {weaknesses.length > 0 && (
        <div className="mt-2.5">
          <p className="text-[9px] font-bold text-ink-4 uppercase tracking-[0.6px] pt-2 mt-1 border-t border-border pb-1" style={{ color: 'var(--color-red)' }}>
            Weaknesses
          </p>
          {weaknesses.slice(0, 3).map((w, i) => (
            <div key={i} className="relative text-[10px] text-ink-2 leading-[1.5] py-[3px] pl-3.5">
              <span className="absolute left-0 top-[9px] w-[5px] h-[5px] rounded-full bg-red" />
              {String(w)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step 5: AI Suggestions ─────────────────────────

function AISuggestionsCard({
  suggestions,
  suggestedBreakdown,
  suggestionMode,
  suggestionCapital,
  suggestionLeverage,
  strategyId,
  strategyStatus,
  hasSuggestedRules,
  suggestionsApplied,
}: {
  suggestions: Record<string, unknown> | undefined;
  suggestedBreakdown: Record<string, unknown> | undefined;
  suggestionMode?: string;
  suggestionCapital?: number;
  suggestionLeverage?: number;
  strategyId: string;
  strategyStatus: string;
  hasSuggestedRules: boolean;
  suggestionsApplied: boolean;
}) {
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const applySuggested = useApplySuggestedRules();
  const notes = suggestions?.notes as string[] | undefined;
  const changes = suggestedBreakdown?.changes as Record<string, string>[] | undefined;

  return (
    <div className="space-y-2">
      {/* Mode / Capital / Leverage Badges */}
      {!!(suggestionMode || suggestionCapital || suggestionLeverage) && (
        <div className="flex items-center gap-3 flex-wrap mb-1">
          {suggestionMode && (
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-bold text-ink-4 uppercase tracking-wider">Mode</span>
              <Badge color={
                suggestionMode === 'conservative' ? 'blue' :
                suggestionMode === 'aggressive' ? 'red' : 'amber'
              }>
                {suggestionMode.charAt(0).toUpperCase() + suggestionMode.slice(1)}
              </Badge>
            </div>
          )}
          {suggestionCapital != null && (
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-bold text-ink-4 uppercase tracking-wider">Capital</span>
              <Badge color="green">${suggestionCapital.toLocaleString()}</Badge>
            </div>
          )}
          {suggestionLeverage != null && (
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-bold text-ink-4 uppercase tracking-wider">Leverage</span>
              <Badge color="cyan">1:{suggestionLeverage}</Badge>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      {!!suggestedBreakdown?.summary && (
        <p className="text-[10px] text-ink-2 leading-relaxed">
          {String(suggestedBreakdown.summary)}
        </p>
      )}

      {/* Proposed Changes */}
      {changes && changes.length > 0 && (
        <div>
          <p className="text-[9px] font-bold text-ink-4 uppercase tracking-[0.6px] pt-2 mt-1 border-t border-border pb-1">Proposed Changes</p>
          <div className="space-y-2">
            {changes.map((c, i) => (
              <div key={i} className="space-y-0.5">
                <p className="text-[10px] font-semibold text-orange">{c.what}</p>
                <p className="text-[9px] text-ink-3">{c.why}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expected Impact */}
      {!!suggestedBreakdown?.expected_impact && (
        <div>
          <p className="text-[9px] font-bold text-ink-4 uppercase tracking-[0.6px] pt-2 mt-1 border-t border-border pb-1">Expected Impact</p>
          <p className="text-[10px] text-ink-2 leading-relaxed">
            {String(suggestedBreakdown.expected_impact)}
          </p>
        </div>
      )}

      {/* Quick Notes */}
      {notes && notes.length > 0 && (
        <div>
          {!!(changes?.length || suggestedBreakdown?.expected_impact) && (
            <p className="text-[9px] font-bold text-ink-4 uppercase tracking-[0.6px] pt-2 mt-1 border-t border-border pb-1">Quick Notes</p>
          )}
          <ul className="space-y-1">
            {notes.map((n, i) => (
              <li key={i} className="flex items-start gap-2">
                <Lightbulb size={10} className="text-orange flex-shrink-0 mt-0.5" />
                <span className="text-[10px] text-ink-2">{n}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Apply Suggested Rules */}
      {hasSuggestedRules && strategyStatus === 'DRAFT' && (
        <div className="border-t border-border pt-3 mt-3">
          {suggestionsApplied ? (
            <div className="flex items-center gap-2 text-green text-[10px] font-semibold">
              <Check size={12} />
              Suggested rules applied — currently active
            </div>
          ) : (
            <>
              {applySuggested.isError && (
                <div className="flex items-center gap-2 text-red text-[10px] mb-2">
                  <AlertTriangle size={10} />
                  <span>Failed to apply suggestions. Please try again.</span>
                </div>
              )}
              <Button
                size="sm"
                className="w-full"
                onClick={() => setShowApplyConfirm(true)}
              >
                <Brain size={12} />
                Apply Suggested Rules
              </Button>
            </>
          )}
        </div>
      )}

      {/* Regenerate Suggestions */}
      {strategyStatus !== 'ARCHIVED' && (
        <RefineSuggestionsForm
          strategyId={strategyId}
          currentMode={suggestionMode}
          currentCapital={suggestionCapital}
          currentLeverage={suggestionLeverage}
        />
      )}

      <Modal
        open={showApplyConfirm}
        onClose={() => setShowApplyConfirm(false)}
        title="Apply AI Suggestions"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowApplyConfirm(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                applySuggested.mutate(strategyId, {
                  onSuccess: () => setShowApplyConfirm(false),
                });
              }}
              disabled={applySuggested.isPending}
            >
              {applySuggested.isPending ? (
                <>
                  <Spinner size="sm" />
                  Applying...
                </>
              ) : (
                <>
                  <Check size={12} />
                  Apply Changes
                </>
              )}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <p className="text-xs text-ink-3">
            This will replace your current rules with the AI-suggested improvements.
          </p>
          <p className="text-xs text-ink-3">
            You can revert to the original rules anytime using{' '}
            <strong className="text-ink">Edit Config &rarr; Reset to Original</strong>.
          </p>
        </div>
      </Modal>
    </div>
  );
}

// ─── Generate Suggestions Form (initial, no suggestions yet) ─

function GenerateSuggestionsForm({
  strategyId,
}: {
  strategyId: string;
}) {
  const qc = useQueryClient();
  const generateSuggestions = useGenerateSuggestions();
  const [mode, setMode] = useState<SuggestionMode>('optimal');
  const [capital, setCapital] = useState('');
  const [leverage, setLeverage] = useState('100');
  const [jobId, setJobId] = useState<string | null>(null);

  const job = useJob('ai-suggestions', jobId);

  // When job completes, refresh strategy data
  useEffect(() => {
    if (job.isComplete) {
      qc.invalidateQueries({ queryKey: ['strategies', strategyId] });
      setJobId(null);
    }
  }, [job.isComplete]);

  useEffect(() => {
    if (job.isFailed) setJobId(null);
  }, [job.isFailed]);

  const handleGenerate = async () => {
    try {
      const result = await generateSuggestions.mutateAsync({
        id: strategyId,
        mode,
        capital: capital ? parseFloat(capital) : undefined,
        leverage: parseInt(leverage, 10),
      });
      setJobId(result.jobId);
    } catch { /* error handled by mutation */ }
  };

  const isProcessing = generateSuggestions.isPending || job.isLoading;
  const progressStage = job.progress?.stage as string | undefined;
  const progressLabel = progressStage
    ? SUGGESTION_STAGES[progressStage] || progressStage
    : 'Generating...';

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-ink-3 leading-relaxed">
        Provide your account details for more calibrated suggestions, then choose a suggestion mode.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Capital (optional)"
          placeholder="e.g. 10000"
          type="number"
          min="0"
          value={capital}
          onChange={(e) => setCapital(e.target.value)}
        />
        <div>
          <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">Leverage</label>
          <select
            value={leverage}
            onChange={(e) => setLeverage(e.target.value)}
            className="w-full bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] cursor-pointer appearance-none"
          >
            {[1, 50, 100, 200, 500, 1000, 2000].map((l) => (
              <option key={l} value={l}>1:{l}</option>
            ))}
          </select>
        </div>
      </div>

      <ModeSelector value={mode} onChange={setMode} />

      {(generateSuggestions.isError || job.isFailed) && (
        <div className="flex items-center gap-2 text-red text-[10px]">
          <AlertTriangle size={10} />
          <span>{job.error || 'Failed to generate suggestions. Please try again.'}</span>
        </div>
      )}

      <Button
        size="sm"
        className="w-full"
        onClick={handleGenerate}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <Spinner size="sm" />
            {progressLabel}
          </>
        ) : (
          <>
            <Brain size={12} />
            Generate Suggestions
          </>
        )}
      </Button>
    </div>
  );
}

// ─── Refine Suggestions Form (has suggestions, collapsed by default) ─

function RefineSuggestionsForm({
  strategyId,
  currentMode,
  currentCapital,
  currentLeverage,
}: {
  strategyId: string;
  currentMode?: string;
  currentCapital?: number;
  currentLeverage?: number;
}) {
  const qc = useQueryClient();
  const generateSuggestions = useGenerateSuggestions();
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState<SuggestionMode>(
    (currentMode as SuggestionMode) || 'optimal',
  );
  const [capital, setCapital] = useState(currentCapital ? String(currentCapital) : '');
  const [leverage, setLeverage] = useState(String(currentLeverage ?? 100));
  const [jobId, setJobId] = useState<string | null>(null);

  const job = useJob('ai-suggestions', jobId);

  useEffect(() => {
    if (job.isComplete) {
      qc.invalidateQueries({ queryKey: ['strategies', strategyId] });
      setJobId(null);
    }
  }, [job.isComplete]);

  useEffect(() => {
    if (job.isFailed) setJobId(null);
  }, [job.isFailed]);

  const handleRegenerate = async () => {
    try {
      const result = await generateSuggestions.mutateAsync({
        id: strategyId,
        mode,
        capital: capital ? parseFloat(capital) : undefined,
        leverage: parseInt(leverage, 10),
      });
      setJobId(result.jobId);
    } catch { /* error handled by mutation */ }
  };

  const isProcessing = generateSuggestions.isPending || job.isLoading;
  const progressLabel = job.progress?.stage
    ? SUGGESTION_STAGES[job.progress.stage as string] || job.progress.stage
    : 'Regenerating...';

  return (
    <div className="border-t border-border pt-3 mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors cursor-pointer w-full"
      >
        <RefreshCw size={10} />
        Refine Suggestions
        {expanded ? <ChevronUp size={10} className="ml-auto" /> : <ChevronDown size={10} className="ml-auto" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Capital (optional)"
              placeholder="e.g. 10000"
              type="number"
              min="0"
              value={capital}
              onChange={(e) => setCapital(e.target.value)}
            />
            <div>
              <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">Leverage</label>
              <select
                value={leverage}
                onChange={(e) => setLeverage(e.target.value)}
                className="w-full bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] cursor-pointer appearance-none"
              >
                {[1, 50, 100, 200, 500, 1000, 2000].map((l) => (
                  <option key={l} value={l}>1:{l}</option>
                ))}
              </select>
            </div>
          </div>

          <ModeSelector value={mode} onChange={setMode} />

          {(generateSuggestions.isError || job.isFailed) && (
            <div className="flex items-center gap-2 text-red text-[10px]">
              <AlertTriangle size={10} />
              <span>{job.error || 'Failed to regenerate. Please try again.'}</span>
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={handleRegenerate}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Spinner size="sm" />
                {progressLabel}
              </>
            ) : (
              <>
                <RefreshCw size={12} />
                Regenerate Suggestions
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Shared: Param Row ──────────────────────────────

function ParamRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-[5px] border-b border-white/[0.04] last:border-b-0 text-[11px]">
      <span className="text-ink-3 font-medium">{label}</span>
      <span className="font-bold font-mono text-right">{value}</span>
    </div>
  );
}

// ─── Version History ────────────────────────────────

function VersionHistory({ strategyId, latestVersion }: { strategyId: string; latestVersion: number }) {
  const { data: versions, isLoading } = useStrategyVersions(strategyId);
  const [expanded, setExpanded] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);
  const [applyTarget, setApplyTarget] = useState<number | null>(null);
  const applyVersion = useApplyVersion();

  if (isLoading) {
    return (
      <div className="glass-sm rounded-lg p-4">
        <div className="flex items-center gap-2">
          <History size={14} className="text-ink-4" />
          <span className="text-[10px] font-bold text-ink-3 uppercase tracking-wider">
            Version History
          </span>
          <Spinner size="sm" />
        </div>
      </div>
    );
  }

  if (!versions || versions.length === 0) return null;

  return (
    <div className="glass-sm rounded-lg">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-surface/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <History size={14} className="text-orange" />
          <span className="text-[10px] font-bold text-ink-3 uppercase tracking-wider">
            Version History
          </span>
          <Badge color="blue">{versions.length} version{versions.length !== 1 ? 's' : ''}</Badge>
        </div>
        {expanded ? <ChevronUp size={14} className="text-ink-4" /> : <ChevronDown size={14} className="text-ink-4" />}
      </button>

      {expanded && (
        <div className="border-t border-border divide-y divide-border">
          {versions.map((v) => {
            const isExpanded = expandedVersion === v.version_number;
            const isLatest = v.version_number === latestVersion;
            return (
              <div key={v.id} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge color={isLatest ? 'green' : 'default'}>
                      v{v.version_number}
                    </Badge>
                    <div>
                      <p className="text-[10px] text-ink-3">
                        {new Date(v.published_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                      {v.change_summary && (
                        <p className="text-[10px] text-ink-4 mt-0.5">{v.change_summary}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isLatest && (
                      <span className="text-[9px] font-bold text-green uppercase">Latest</span>
                    )}
                    <button
                      onClick={() => setExpandedVersion(isExpanded ? null : v.version_number)}
                      className="text-[10px] text-orange hover:text-orange-light font-semibold transition-colors cursor-pointer"
                    >
                      {isExpanded ? 'Hide Rules' : 'View Rules'}
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <VersionRulesPreview
                    strategyId={strategyId}
                    versionNumber={v.version_number}
                    isLatest={isLatest}
                    onApply={() => setApplyTarget(v.version_number)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={applyTarget !== null}
        onClose={() => setApplyTarget(null)}
        title={`Revert to v${applyTarget}`}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setApplyTarget(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (applyTarget !== null) {
                  applyVersion.mutate({ id: strategyId, versionNumber: applyTarget });
                  setApplyTarget(null);
                }
              }}
              disabled={applyVersion.isPending}
            >
              <RotateCcw size={12} />
              Apply v{applyTarget}
            </Button>
          </>
        }
      >
        <p className="text-xs text-ink-3">
          Replace current rules with v{applyTarget}? The strategy will become a draft and any unsaved changes will be overwritten.
        </p>
      </Modal>
    </div>
  );
}

function VersionRulesPreview({
  strategyId,
  versionNumber,
  isLatest,
  onApply,
}: {
  strategyId: string;
  versionNumber: number;
  isLatest: boolean;
  onApply: () => void;
}) {
  const { data: version, isLoading } = useStrategyVersion(strategyId, versionNumber);

  if (isLoading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-[10px] text-ink-4">
        <Spinner size="sm" /> Loading rules...
      </div>
    );
  }

  if (!version) return null;

  const rules = version.rules as Record<string, unknown>;
  const marketData = (rules?.market_data ?? {}) as Record<string, unknown>;
  const entry = rules?.entry as Record<string, unknown> | undefined;
  const exit = rules?.exit as Record<string, unknown> | undefined;
  const risk = rules?.risk as Record<string, unknown> | undefined;
  const entryRules: Record<string, unknown>[] = Array.isArray(entry?.rules) ? entry.rules : [];
  const exitRules: Record<string, unknown>[] = Array.isArray(exit?.rules) ? exit.rules : [];
  const grid = entry?.grid as Record<string, unknown> | undefined;
  const trailingStop = exit?.trailingStop as Record<string, unknown> | undefined;
  const equitySl = exit?.equityStopLossPct as number | undefined;
  const indicators = extractIndicators(rules as Record<string, unknown>);

  return (
    <div className="mt-3 space-y-3">
      {/* Timeframe + type badges */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {!!marketData.timeframe && <Badge color="blue">{String(marketData.timeframe)}</Badge>}
        {!!rules.strategy_type && (
          <Badge color="orange">
            {rules.strategy_type === 'CONDITION_ENGINE' ? 'Condition Engine' : String(rules.strategy_type)}
          </Badge>
        )}
      </div>

      {/* Entry Rules */}
      {entryRules.length > 0 && (
        <div className="bg-surface/30 rounded-lg p-3">
          <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-2">Entry Rules</p>
          <div className="space-y-2.5">
            {entryRules.map((rule, i) => {
              const direction = String(rule.direction);
              return (
                <div key={i} className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge color={direction === 'BUY' ? 'green' : direction === 'SELL' ? 'red' : 'blue'}>
                      {direction === 'SIGNAL' ? 'SIGNAL' : direction}
                    </Badge>
                    {!!rule.maxPositions && (
                      <span className="text-[10px] text-ink-4">max {String(rule.maxPositions)} pos</span>
                    )}
                    {!!rule.cooldownCandles && (
                      <span className="text-[10px] text-ink-4">{String(rule.cooldownCandles)} bar cooldown</span>
                    )}
                  </div>
                  {direction === 'SIGNAL' ? (
                    <div className="space-y-1 pl-3 border-l-2 border-glass-border">
                      {!!rule.buyCondition && (
                        <div className="flex items-start gap-1.5">
                          <Badge color="green">BUY</Badge>
                          <span className="text-[10px] leading-relaxed">{condToSentence(rule.buyCondition)}</span>
                        </div>
                      )}
                      {!!rule.sellCondition && (
                        <div className="flex items-start gap-1.5">
                          <Badge color="red">SELL</Badge>
                          <span className="text-[10px] leading-relaxed">{condToSentence(rule.sellCondition)}</span>
                        </div>
                      )}
                    </div>
                  ) : rule.conditions ? (
                    <div className="pl-3 border-l-2 border-glass-border">
                      <span className="text-[10px] leading-relaxed">{condToSentence(rule.conditions)}</span>
                    </div>
                  ) : null}
                  {!!rule.lotSizing && (
                    <div className="text-[10px] text-ink-3 pl-3">Lot: {fmtLot(rule.lotSizing)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Exit Rules */}
      {exitRules.length > 0 && (
        <div className="bg-surface/30 rounded-lg p-3">
          <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-2">Exit Rules</p>
          <div className="space-y-2.5">
            {exitRules.map((rule, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge color="amber">{String(rule.action ?? 'CLOSE_ALL').replace(/_/g, ' ')}</Badge>
                  {rule.closePct != null && (rule.closePct as number) < 100 && (
                    <span className="text-[10px] text-ink-4">{rule.closePct as number}% of each</span>
                  )}
                </div>
                {!!rule.conditions && (
                  <div className="pl-3 border-l-2 border-glass-border">
                    <span className="text-[10px] leading-relaxed">{condToSentence(rule.conditions)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk & Execution */}
      {(grid?.enabled === true || !!trailingStop?.enabled || equitySl != null || !!risk?.maxTotalPositions) && (
        <div className="bg-surface/30 rounded-lg p-3">
          <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-2">Risk & Execution</p>
          <div className="space-y-2">
            {grid?.enabled === true && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge color="violet">Grid</Badge>
                <span className="text-[10px] text-ink-3 font-mono">
                  {grid.direction !== 'FOLLOW_ENTRY' && <>{String(grid.direction)} &middot; </>}
                  step {String(grid.stepValue)}{' '}
                  {grid.stepType === 'PERCENT' ? '%' : grid.stepType === 'ATR_MULTIPLE' ? 'ATR' : 'pips'}
                  {grid.maxGridLevels ? <> &middot; max {String(grid.maxGridLevels)} levels</> : ''}
                </span>
                {!!grid.lotSizing && (
                  <span className="text-[10px] text-ink-4">&middot; {fmtLot(grid.lotSizing)}</span>
                )}
              </div>
            )}
            {!!trailingStop?.enabled && (
              <div className="flex items-center gap-1.5">
                <Badge color="cyan">Trailing Stop{trailingStop.basketMode ? ' (Basket)' : ''}</Badge>
                <span className="text-[10px] text-ink-3 font-mono">
                  {trailingStop.type === 'ATR_MULTIPLE'
                    ? `ATR\u00D7${trailingStop.value} (period ${trailingStop.atrPeriod ?? 14})`
                    : trailingStop.type === 'PERCENT'
                      ? `${trailingStop.value}%${trailingStop.basketMode ? ' of peak' : ''}`
                      : trailingStop.basketMode
                        ? `$${trailingStop.value} pullback`
                        : `${trailingStop.value} pips`}
                </span>
              </div>
            )}
            {!!risk?.maxTotalPositions && (
              <div className="flex items-center gap-1.5">
                <Badge color="amber">Max Positions</Badge>
                <span className="text-[10px] text-ink-3 font-mono">{String(risk.maxTotalPositions)}</span>
              </div>
            )}
            {equitySl != null && (
              <div className="flex items-center gap-1.5">
                <Badge color="red">Equity SL</Badge>
                <span className="text-[10px] text-ink-3 font-mono">{equitySl}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Indicators */}
      {indicators.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1.5">Indicators</p>
          <div className="flex flex-wrap gap-1.5">
            {indicators.map((ind) => (
              <Badge key={ind} color="violet">{ind}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Apply button (hidden on latest) */}
      {!isLatest && (
        <Button variant="ghost" size="sm" className="w-full mt-1" onClick={onApply}>
          <RotateCcw size={12} />
          Apply v{versionNumber} Rules
        </Button>
      )}
    </div>
  );
}

// ─── Backtest Panel ─────────────────────────────────

function BacktestPanel({
  strategyId,
  strategy,
  onHasWarnings,
}: {
  strategyId: string;
  strategy: NonNullable<ReturnType<typeof useStrategy>['data']>;
  onHasWarnings?: (hasWarnings: boolean) => void;
}) {
  const runBacktest = useRunBacktest();
  const rules = strategy.rules as Record<string, unknown> | null;
  const snapshot = strategy.analysis_snapshot as Record<string, unknown> | null;
  const snapshotCapital = snapshot?.suggestion_capital as number | undefined;
  const snapshotLeverage = snapshot?.suggestion_leverage as number | undefined;

  // Persist backtest config per strategy in localStorage
  const storageKey = `backtest-config-${strategyId}`;
  const saved = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch { return {}; }
  }, [storageKey]);

  const [days, setDays] = useState(saved.days ?? 30);
  const [dateMode, setDateMode] = useState<'preset' | 'custom'>(saved.dateMode ?? 'preset');
  const [startDate, setStartDate] = useState(saved.startDate ?? '');
  const [endDate, setEndDate] = useState(saved.endDate ?? '');
  const [capital, setCapital] = useState(String(saved.capital ?? snapshotCapital ?? 10000));
  const [leverage, setLeverage] = useState(String(saved.leverage ?? snapshotLeverage ?? 100));
  const [jobId, setJobId] = useState<string | null>(null);

  const job = useJob<BacktestReport>('backtest', jobId);

  // Save config whenever it changes
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({
      days, dateMode, startDate, endDate,
      capital: parseFloat(capital) || 10000,
      leverage: parseInt(leverage) || 100,
    }));
  }, [days, dateMode, startDate, endDate, capital, leverage, storageKey]);

  // Only apply snapshot defaults on first load (when no saved config)
  useEffect(() => {
    if (!saved.capital && snapshotCapital != null) setCapital(String(snapshotCapital));
  }, [snapshotCapital]);
  useEffect(() => {
    if (!saved.leverage && snapshotLeverage != null) setLeverage(String(snapshotLeverage));
  }, [snapshotLeverage]);
  const stats = (snapshot?.stats ?? {}) as Record<string, unknown>;
  // Prefer strategy.symbol (user-editable) over analysis_snapshot.stats.symbol (auto-detected)
  const symbol = strategy.symbol || (typeof stats.symbol === 'string' ? stats.symbol : undefined);

  const handleRun = async () => {
    const recommendedFriction = stats.recommendedFriction as BacktestParams['friction'] | null;
    const params: BacktestParams = {
      ...(dateMode === 'custom'
        ? { startDate, endDate: endDate || undefined }
        : { days }),
      capital: parseFloat(capital) || 10000,
      leverage: parseInt(leverage) || 1,
      symbol,
      timeframe: ((rules?.market_data as Record<string, unknown>)?.timeframe as string) ?? undefined,
      ...(recommendedFriction ? { friction: recommendedFriction } : {}),
    };
    try {
      const result = await runBacktest.mutateAsync({ id: strategyId, params });
      setJobId(result.jobId);
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { /* error handled by mutation */ }
  };

  const isProcessing = runBacktest.isPending || job.isLoading;
  const progressStage = job.progress?.stage as string | undefined;
  const progressLabel = progressStage
    ? BACKTEST_STAGES[progressStage] || progressStage
    : 'Running...';
  const backtestResult = job.isComplete ? job.result : undefined;

  useEffect(() => {
    onHasWarnings?.(!!backtestResult);
  }, [!!backtestResult, onHasWarnings]);

  return (
    <div className="glass-sm rounded-lg overflow-hidden">
      {/* Form */}
      <div className="p-4 border-b border-border">
        <h3 className="text-xs font-extrabold uppercase tracking-wider mb-3 flex items-center gap-2">
          <FlaskConical size={14} className="text-orange" />
          Backtest
        </h3>

        <div className="space-y-3">
          {/* Period Pills */}
          <div>
            <label className="block text-[10px] font-semibold text-ink-3 mb-1.5">Period</label>
            <div className="flex gap-1">
              {[7, 30, 365, 730].map((d) => {
                const maxDays = getMaxBacktestDays(symbol);
                const isDisabled = d > maxDays;
                return (
                  <button
                    key={d}
                    onClick={() => { if (!isDisabled) { setDays(d); setDateMode('preset'); } }}
                    disabled={isDisabled}
                    title={isDisabled ? `Deriv synthetics only support up to ${maxDays} days of historical data` : undefined}
                    className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-all flex-1 ${
                      isDisabled
                        ? 'bg-surface text-ink-3/40 border border-glass-border cursor-not-allowed opacity-50'
                        : dateMode === 'preset' && days === d
                          ? 'bg-orange-bg text-orange border border-orange/20 cursor-pointer'
                          : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2 cursor-pointer'
                    }`}
                  >
                    {d === 7 ? '7d' : d === 30 ? '30d' : d === 365 ? '1y' : '2y'}
                  </button>
                );
              })}
              <button
                onClick={() => setDateMode('custom')}
                className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-all cursor-pointer flex-1 ${
                  dateMode === 'custom'
                    ? 'bg-orange-bg text-orange border border-orange/20'
                    : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
                }`}
              >
                Custom
              </button>
            </div>
            {dateMode === 'custom' && (
              <div className="flex gap-2 mt-2">
                <div className="flex-1">
                  <DatePicker label="Start" value={startDate} onChange={setStartDate} max={endDate || new Date().toISOString().slice(0, 10)} />
                </div>
                <div className="flex-1">
                  <DatePicker label="End" value={endDate} onChange={setEndDate} min={startDate || undefined} max={new Date().toISOString().slice(0, 10)} />
                </div>
              </div>
            )}
          </div>

          <Input
            label="Capital"
            type="number"
            value={capital}
            onChange={(e) => setCapital(e.target.value)}
            placeholder="10000"
          />

          <div>
            <label className="block text-[10px] font-semibold text-ink-3 mb-1.5">Leverage</label>
            <select
              value={leverage}
              onChange={(e) => setLeverage(e.target.value)}
              className="w-full bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] cursor-pointer appearance-none"
            >
              {[1, 50, 100, 200, 500, 1000, 2000].map((l) => (
                <option key={l} value={l}>
                  1:{l}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <Button className="flex-1" onClick={handleRun} disabled={isProcessing || (dateMode === 'custom' && (!startDate || !endDate))}>
              {isProcessing ? (
                <>
                  <Spinner size="sm" />
                  {progressLabel}
                </>
              ) : (
                <>
                  <Play size={14} />
                  Run Backtest
                </>
              )}
            </Button>
            <Link to={`/strategies/${strategyId}/compare`}>
              <Button variant="ghost" className="h-full">
                <FlaskConical size={14} />
              </Button>
            </Link>
          </div>
          {dateMode === 'custom' && (!startDate || !endDate) && (
            <p className="text-[10px] text-amber font-semibold mt-1">Both dates are required</p>
          )}

          {(runBacktest.isError || job.isFailed) && (
            <div className="flex items-center gap-2 bg-red-bg rounded-md px-3 py-2">
              <AlertTriangle size={12} className="text-red flex-shrink-0" />
              <p className="text-[10px] text-red font-semibold">
                {friendlyError(job.error ?? (runBacktest.error as Error)?.message, 'Backtest failed')}
              </p>
            </div>
          )}

        </div>
      </div>

      {/* Results — show spinner overlay on top of previous results */}
      {(isProcessing || backtestResult || strategy.latest_backtest) && (
        <div className={`relative ${isProcessing ? 'min-h-[120px]' : ''}`}>
          {isProcessing && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-bg/70 rounded-lg backdrop-blur-sm">
              <Spinner size="md" />
              <p className="text-xs text-ink-4 font-semibold">{progressLabel}</p>
            </div>
          )}
          {backtestResult && <BacktestResults report={backtestResult} loading={false} />}
          {!backtestResult && !isProcessing && strategy.latest_backtest && (
            <SavedBacktestSummary summary={strategy.latest_backtest} />
          )}
        </div>
      )}
    </div>
  );
}

function SavedBacktestSummary({ summary }: { summary: BacktestSummary }) {
  const netProfit = summary.netProfit ?? 0;
  const returnPct = summary.returnPct ?? 0;
  const winRate = summary.winRate ?? 0;
  const profitFactor = summary.profitFactor ?? 0;
  const sharpeRatio = summary.sharpeRatio ?? 0;
  const maxDrawdown = summary.maxDrawdown ?? 0;
  const avgTradePnl = summary.avgTradePnl ?? 0;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <FlaskConical size={12} className="text-purple" />
          <span className="text-[10px] font-bold text-purple uppercase tracking-wider">Last Backtest</span>
        </div>
        {summary.backtestRanAt && (
          <span className="text-[10px] text-ink-4">{timeAgo(summary.backtestRanAt)}</span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] text-ink-4 uppercase tracking-wider">Net Profit</p>
          <p className={`text-lg font-extrabold font-mono ${pnlColor(netProfit)}`}>
            {formatPnl(netProfit)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-ink-4 uppercase tracking-wider">Return</p>
          <p className={`text-sm font-bold font-mono ${pnlColor(returnPct)}`}>
            {formatPct(returnPct)}
          </p>
        </div>
      </div>

      {summary.botStopped && (
        <div className="flex items-center gap-2 bg-red-bg rounded-md px-3 py-2">
          <AlertTriangle size={12} className="text-red flex-shrink-0" />
          <p className="text-[10px] text-red font-semibold">
            Bot stopped: {summary.stopReason}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <StatCell label="Trades" value={String(summary.totalTrades ?? 0)} />
        <StatCell label="Win Rate" value={`${winRate.toFixed(1)}%`} />
        <StatCell
          label="Profit Factor"
          value={`${profitFactor.toFixed(2)}×`}
          color={pnlColor(profitFactor - 1)}
        />
        <StatCell label="Sharpe" value={sharpeRatio.toFixed(2)} />
        <StatCell label="Max DD" value={`${maxDrawdown.toFixed(2)}%`} color="text-red" />
        <StatCell label="Avg Trade" value={formatPnl(avgTradePnl)} color={pnlColor(avgTradePnl)} />
        <StatCell label="Max Consec. L" value={String(summary.maxConsecutiveLosses ?? 0)} />
      </div>

      {(summary.dataTimeframe || summary.dataStartDate) && (
        <div className="flex items-center gap-2 flex-wrap">
          {summary.dataTimeframe && (
            <span className="text-[9px] text-ink-4 bg-surface/50 rounded px-1.5 py-0.5">
              {summary.dataTimeframe}
            </span>
          )}
          {summary.dataStartDate && summary.dataEndDate && (
            <span className="text-[9px] text-ink-4 bg-surface/50 rounded px-1.5 py-0.5">
              {summary.dataStartDate.slice(0, 10)} — {summary.dataEndDate.slice(0, 10)}
            </span>
          )}
          {summary.dataSource && (
            <span className="text-[9px] text-ink-4 bg-surface/50 rounded px-1.5 py-0.5">
              {summary.dataSource}
            </span>
          )}
        </div>
      )}

      <p className="text-[9px] text-ink-4 text-center">Run a new backtest to see full results with equity curve and trades.</p>
    </div>
  );
}

function BacktestResults({ report, loading }: { report: BacktestReport; loading?: boolean }) {
  const [showTrades, setShowTrades] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const prevReportRef = useRef(report);

  useEffect(() => {
    if (report !== prevReportRef.current) {
      prevReportRef.current = report;
      setAnimKey((k) => k + 1);
      // Reset trade page on new report
    }
  }, [report]);

  return (
    <div className="relative">
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-10 bg-surface/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 rounded-b-lg">
          <Spinner size="md" />
          <p className="text-xs text-ink-4 font-semibold">Running backtest...</p>
        </div>
      )}
    <div
      key={animKey}
      className="p-4 space-y-3 animate-fade-in"
    >
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] text-ink-4 uppercase tracking-wider">Net Profit</p>
          <p className={`text-lg font-extrabold font-mono ${pnlColor(report.netProfit ?? 0)}`}>
            {formatPnl(report.netProfit ?? 0)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-ink-4 uppercase tracking-wider">Return</p>
          <p className={`text-sm font-bold font-mono ${pnlColor(report.returnPct ?? 0)}`}>
            {formatPct(report.returnPct ?? 0)}
          </p>
        </div>
      </div>

      {report.botStopped && (
        <div className="flex items-center gap-2 bg-red-bg rounded-md px-3 py-2">
          <AlertTriangle size={12} className="text-red flex-shrink-0" />
          <p className="text-[10px] text-red font-semibold">
            Bot stopped: {report.stopReason}
          </p>
        </div>
      )}

      {report.warning && (
        <div className="flex items-center gap-2 bg-amber-bg rounded-md px-3 py-2">
          <AlertTriangle size={12} className="text-amber flex-shrink-0" />
          <p className="text-[10px] text-amber font-semibold">
            {report.warning}
          </p>
        </div>
      )}

      {/* Structured warnings from cost analysis */}
      {report.warnings && report.warnings.length > 0 && (
        <div className="space-y-1.5">
          {report.warnings.map((w: { severity: string; code: string; message: string }, i: number) => (
            <div
              key={i}
              className={`flex items-start gap-2 rounded-md px-3 py-2 ${
                w.severity === 'CRITICAL'
                  ? 'bg-red-bg'
                  : w.severity === 'WARNING'
                    ? 'bg-amber-bg'
                    : 'bg-surface-2'
              }`}
            >
              <AlertTriangle
                size={11}
                className={`flex-shrink-0 mt-0.5 ${
                  w.severity === 'CRITICAL'
                    ? 'text-red'
                    : w.severity === 'WARNING'
                      ? 'text-amber'
                      : 'text-ink-3'
                }`}
              />
              <p
                className={`text-[10px] font-semibold ${
                  w.severity === 'CRITICAL'
                    ? 'text-red'
                    : w.severity === 'WARNING'
                      ? 'text-amber'
                      : 'text-ink-3'
                }`}
              >
                {w.message}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Equity Curve */}
      {report.equityCurve && report.equityCurve.length > 1 && (
        <div>
          <p className="text-[8px] text-ink-4 uppercase tracking-wider mb-1">Equity Curve</p>
          <EquityCurve data={report.equityCurve} initialCapital={report.initialCapital} />
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2">
        <StatCell label="Trades" value={String(report.totalTrades ?? 0)} />
        <StatCell label="Win Rate" value={`${(report.winRate ?? 0).toFixed(1)}%`} />
        <StatCell
          label="Profit Factor"
          value={`${(report.profitFactor ?? 0).toFixed(2)}×`}
          color={pnlColor((report.profitFactor ?? 0) - 1)}
        />
        <StatCell label="Sharpe" value={(report.sharpeRatio ?? 0).toFixed(2)} />
        <StatCell label="Max DD" value={`${(report.maxDrawdown ?? 0).toFixed(2)}%`} color="text-red" />
        <StatCell label="Avg Trade" value={formatPnl(report.avgTradePnl ?? 0)} color={pnlColor(report.avgTradePnl ?? 0)} />
        <StatCell label="Avg Win" value={formatUsd(report.avgWinPnl ?? 0)} color="text-green" />
        <StatCell label="Avg Loss" value={formatPnl(report.avgLossPnl ?? 0)} color="text-red" />
        <StatCell label="Max Consec. L" value={String(report.maxConsecutiveLosses ?? 0)} />
        <StatCell
          label="Avg Hold"
          value={formatDuration(report.avgHoldingTimeMs ?? 0)}
        />
      </div>

      {/* Margin rejection warning */}
      {(report.marginRejections ?? 0) > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 text-[10px] text-amber-400">
          <span className="font-semibold">{report.marginRejections} trade signal(s) rejected</span> due to
          insufficient margin. Lot sizing may be too large for the capital/leverage. Try reducing baseLotRiskFactor
          or increasing leverage.
        </div>
      )}

      {/* Trades modal trigger */}
      {report.trades.length > 0 && (
        <div>
          <button
            onClick={() => setShowTrades(true)}
            className="text-[10px] text-orange font-semibold cursor-pointer flex items-center gap-1 w-full justify-center py-1"
          >
            <Activity size={10} />
            View {report.totalTrades} trades
            {report.trades.length < report.totalTrades && (
              <span className="text-ink-4 font-normal">(latest {report.trades.length} shown)</span>
            )}
          </button>

          <TradeHistoryModal
            open={showTrades}
            onClose={() => setShowTrades(false)}
            trades={report.trades}
            totalTrades={report.totalTrades}
            netProfit={report.netProfit ?? 0}
          />
        </div>
      )}
    </div>
    </div>
  );
}

function StatCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="bg-surface/50 rounded-md p-1.5 text-center">
      <p className="text-[8px] text-ink-4 uppercase tracking-wider">{label}</p>
      <p className={`text-[10px] font-bold font-mono ${color ?? 'text-ink'}`}>{value}</p>
    </div>
  );
}

